import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaCheck, FaTimes, FaInfoCircle, FaExclamationTriangle } from 'react-icons/fa';

const ToastContext = createContext(null);

const slideIn = keyframes`
    from {
        transform: translateX(100%);
        opacity: 0;
    }
    to {
        transform: translateX(0);
        opacity: 1;
    }
`;

const slideOut = keyframes`
    from {
        transform: translateX(0);
        opacity: 1;
    }
    to {
        transform: translateX(100%);
        opacity: 0;
    }
`;

const ToastContainer = styled.div`
    position: fixed;
    bottom: 24px;
    right: 24px;
    z-index: 9999;
    display: flex;
    flex-direction: column;
    gap: 12px;
    pointer-events: none;
`;

const ToastItem = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    min-width: 300px;
    max-width: 420px;
    padding: 14px 16px;
    background: #fff;
    border-radius: 5px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    border-left: 4px solid ${(p) => {
        switch (p.$type) {
            case 'success': return '#673AB7';
            case 'error': return '#b33a3a';
            case 'warning': return '#c77a00';
            default: return '#673AB7';
        }
    }};
    animation: ${(p) => (p.$leaving ? slideOut : slideIn)} 0.3s ease forwards;
    pointer-events: auto;
`;

const IconWrapper = styled.div`
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: #fff;
    background: ${(p) => {
        switch (p.$type) {
            case 'success': return '#673AB7';
            case 'error': return '#b33a3a';
            case 'warning': return '#c77a00';
            default: return '#673AB7';
        }
    }};

    svg {
        width: 12px;
        height: 12px;
    }
`;

const ToastContent = styled.div`
    flex: 1;
    min-width: 0;
`;

const ToastTitle = styled.p`
    margin: 0 0 2px 0;
    font-size: 0.9rem;
    font-weight: 600;
    color: #1a2332;
    font-family: 'Outfit', sans-serif;
`;

const ToastMessage = styled.p`
    margin: 0;
    font-size: 0.85rem;
    color: #6b7c8d;
    line-height: 1.4;
`;

const CloseButton = styled.button`
    flex-shrink: 0;
    padding: 4px;
    background: none;
    border: none;
    color: #8a9ba8;
    cursor: pointer;
    border-radius: 4px;
    transition: color 0.2s, background 0.2s;

    &:hover {
        color: #1a2332;
        background: #f0f2f5;
    }

    svg {
        width: 14px;
        height: 14px;
        display: block;
    }
`;

const getIcon = (type) => {
    switch (type) {
        case 'success': return <FaCheck />;
        case 'error': return <FaTimes />;
        case 'warning': return <FaExclamationTriangle />;
        default: return <FaInfoCircle />;
    }
};

const getDefaultTitle = (type) => {
    switch (type) {
        case 'success': return 'Succès';
        case 'error': return 'Erreur';
        case 'warning': return 'Attention';
        default: return 'Information';
    }
};

let toastId = 0;

export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    const removeToast = useCallback((id) => {
        setToasts((prev) =>
            prev.map((t) => (t.id === id ? { ...t, leaving: true } : t))
        );
        setTimeout(() => {
            setToasts((prev) => prev.filter((t) => t.id !== id));
        }, 300);
    }, []);

    const addToast = useCallback(({ type = 'info', title, message, duration = 4000 }) => {
        const id = ++toastId;
        const toast = {
            id,
            type,
            title: title ?? getDefaultTitle(type),
            message,
            leaving: false,
        };
        setToasts((prev) => [...prev, toast]);

        if (duration > 0) {
            setTimeout(() => removeToast(id), duration);
        }

        return id;
    }, [removeToast]);

    const toast = useMemo(
        () => ({
            success: (message, options = {}) => addToast({ type: 'success', message, ...options }),
            error: (message, options = {}) => addToast({ type: 'error', message, ...options }),
            warning: (message, options = {}) => addToast({ type: 'warning', message, ...options }),
            info: (message, options = {}) => addToast({ type: 'info', message, ...options }),
            dismiss: removeToast,
        }),
        [addToast, removeToast]
    );

    return (
        <ToastContext.Provider value={toast}>
            {children}
            <ToastContainer>
                {toasts.map((t) => (
                    <ToastItem key={t.id} $type={t.type} $leaving={t.leaving}>
                        <IconWrapper $type={t.type}>
                            {getIcon(t.type)}
                        </IconWrapper>
                        <ToastContent>
                            <ToastTitle>{t.title}</ToastTitle>
                            {t.message && <ToastMessage>{t.message}</ToastMessage>}
                        </ToastContent>
                        <CloseButton onClick={() => removeToast(t.id)} aria-label="Fermer">
                            <FaTimes />
                        </CloseButton>
                    </ToastItem>
                ))}
            </ToastContainer>
        </ToastContext.Provider>
    );
};

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

export default ToastProvider;
